package com.fantasytenis.shared.domain.contests.usecases

import com.fantasytenis.shared.core.network.ApiResponse
import com.fantasytenis.shared.data.contests.api.ContestsApi
import com.fantasytenis.shared.data.contests.dto.AdminDeleteEntryRequest
import com.fantasytenis.shared.data.contests.dto.AdminDeleteEntryResponse
import com.fantasytenis.shared.data.contests.dto.SetContestLockOverrideRequest
import com.fantasytenis.shared.data.contests.dto.SlateDto
import com.fantasytenis.shared.data.contests.repository.ContestsRepository
import com.fantasytenis.shared.domain.bracket.entity.LockOverride
import com.fantasytenis.shared.domain.contests.entity.ContestEntity
import com.fantasytenis.shared.domain.contests.entity.ContestStatus
import com.fantasytenis.shared.domain.contests.entity.ContestType
import com.fantasytenis.shared.domain.contests.entity.EntryEntity
import com.fantasytenis.shared.domain.tennis.entity.TennisPlayerEntity

/**
 * Get a contest by ID.
 */
class GetContestUseCase(
    private val contestsRepository: ContestsRepository
) {
    suspend operator fun invoke(contestId: String): Result<ContestEntity> {
        return contestsRepository.getContest(contestId)
    }
}

/**
 * List contests with optional filters.
 */
class ListContestsUseCase(
    private val contestsRepository: ContestsRepository
) {
    suspend operator fun invoke(
        status: Int? = null,
        type: Int? = null,
        slateId: String? = null,
        limit: Int = 20,
        offset: Int = 0
    ): Result<List<ContestEntity>> {
        return contestsRepository.listContests(status, type, slateId, limit, offset)
    }
}

/**
 * Enter a contest.
 */
class EnterContestUseCase(
    private val contestsRepository: ContestsRepository
) {
    suspend operator fun invoke(contestId: String): Result<EntryEntity> {
        return contestsRepository.enterContest(contestId)
    }
}

/**
 * Cancel a contest entry.
 */
class CancelContestEntryUseCase(
    private val contestsRepository: ContestsRepository
) {
    suspend operator fun invoke(entryId: String): Result<EntryEntity> {
        return contestsRepository.cancelEntry(entryId)
    }
}

/**
 * Get current user's contest entries.
 */
class GetUserContestEntriesUseCase(
    private val contestsRepository: ContestsRepository
) {
    suspend operator fun invoke(contestId: String? = null): Result<List<EntryEntity>> {
        return contestsRepository.getUserEntries(contestId)
    }
}

/**
 * Get contest leaderboard.
 */
class GetContestLeaderboardUseCase(
    private val contestsRepository: ContestsRepository
) {
    suspend operator fun invoke(
        contestId: String,
        limit: Int = 50,
        offset: Int = 0
    ): Result<List<EntryEntity>> {
        return contestsRepository.getContestLeaderboard(contestId, limit, offset)
    }
}

/**
 * Get slate by ID.
 */
class GetSlateUseCase(
    private val contestsRepository: ContestsRepository
) {
    suspend operator fun invoke(slateId: String): Result<SlateDto> {
        return contestsRepository.getSlate(slateId)
    }
}

/**
 * Get players for a slate with proper costs and gender.
 */
class GetSlatePlayersUseCase(
    private val contestsRepository: ContestsRepository
) {
    suspend operator fun invoke(slateId: String, gender: String): Result<List<TennisPlayerEntity>> {
        return contestsRepository.getSlatePlayers(slateId, gender)
    }
}

// ===== ADMIN USE CASES =====

/**
 * Set lock override for a contest (SuperAdmin only).
 * Allows admins to manually unlock/lock contests regardless of time-based lock.
 */
class SetContestLockOverrideUseCase(
    private val contestsApi: ContestsApi
) {
    /**
     * @param contestId The ID of the contest
     * @param lockOverride The lock override value (NONE, FORCE_UNLOCKED, FORCE_LOCKED)
     * @param reason Optional reason for the override
     * @return The updated ContestEntity
     */
    suspend operator fun invoke(
        contestId: String,
        lockOverride: LockOverride,
        reason: String? = null
    ): Result<ContestEntity> {
        val apiValue = when (lockOverride) {
            LockOverride.NONE -> 1
            LockOverride.FORCE_UNLOCKED -> 2
            LockOverride.FORCE_LOCKED -> 3
        }

        val request = SetContestLockOverrideRequest(
            lockOverride = apiValue,
            reason = reason
        )

        return when (val response = contestsApi.setLockOverride(contestId, request)) {
            is ApiResponse.Success -> {
                val dto = response.data
                Result.success(
                    ContestEntity(
                        id = dto.id,
                        name = dto.name,
                        tournamentId = dto.slateId,
                        tournamentName = "", // Not returned in DTO
                        startTime = 0, // Will be parsed from ISO string if needed
                        rosterLockTime = 0,
                        lineupLockTime = dto.lineupLockTime,
                        entryFee = dto.entryFee / 100.0,
                        prizePool = dto.prizePool / 100.0,
                        maxEntries = dto.maxEntries,
                        currentEntries = dto.currentEntries,
                        status = ContestStatus.entries.getOrNull(dto.status - 1) ?: ContestStatus.UPCOMING,
                        contestType = ContestType.entries.getOrNull(dto.type - 1) ?: ContestType.GPP,
                        salaryCap = dto.salaryCap,
                        rosterSize = 5, // Default
                        prizeStructure = emptyList(),
                        isGuaranteed = dto.isGuaranteed,
                        isFeatured = dto.isFeatured,
                        lockOverride = LockOverride.fromApiValue(dto.lockOverride),
                        lockOverrideBy = dto.lockOverrideBy.ifBlank { null },
                        lockOverrideAt = dto.lockOverrideAt.ifBlank { null },
                        lockOverrideReason = dto.lockOverrideReason.ifBlank { null }
                    )
                )
            }
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }
}

/**
 * Admin delete a contest entry (SuperAdmin only).
 * This allows superadmins to remove any entry from a contest.
 */
class AdminDeleteContestEntryUseCase(
    private val contestsApi: ContestsApi
) {
    /**
     * @param entryId The ID of the entry to delete
     * @param reason The reason for deletion (required for audit)
     * @return The admin delete response with refund info
     */
    suspend operator fun invoke(
        entryId: String,
        reason: String
    ): Result<AdminDeleteEntryResponse> {
        val request = AdminDeleteEntryRequest(reason = reason)

        return when (val response = contestsApi.adminDeleteEntry(entryId, request)) {
            is ApiResponse.Success -> Result.success(response.data)
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }
}
